import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import PageHeader from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import { observer } from "mobx-react";
import EmptySpace from "../../components/common/EmptySpace";
import Loading from "../../components/common/Loading";
import { FileUploadModal } from "../../components/common/FileUploadModal";
import { Image } from "../../components/UI/Image";
import PopupState, { bindPopover, bindTrigger } from "material-ui-popup-state";
import { IconButton, Popover, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "notistack";

const IconsList: React.FC = observer(() => {
  const { configStore } = useStores();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalHandler = () => {
    setIsModalOpen((open) => !open);
  };
  const { enqueueSnackbar } = useSnackbar();

  const fetchIconsList = useCallback(async () => {
    await configStore.fetchIconsList().catch((err) => {
      enqueueSnackbar(err.message, {
        variant: "error",
      });
    });
  }, [enqueueSnackbar, configStore]);

  useEffect(() => {
    fetchIconsList();
  }, [fetchIconsList]);

  const handleUploadCompleted = useCallback(
    async (imageUrls: string[]) => {
      await configStore
        .addIcon({ iconUrl: imageUrls[0] })
        .then(() => {
          enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
            variant: "success",
          });
        })
        .catch((err) => {
          enqueueSnackbar(err.message, {
            variant: "error",
          });
        });
      modalHandler();
      fetchIconsList();
    },
    [enqueueSnackbar, fetchIconsList, configStore, t]
  );

  const handleDeleteProgram = useCallback(
    async (id: number) => {
      await configStore
        .deleteIcon(id)
        .then(() => {
          enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
            variant: "success",
          });
        })
        .catch((err) => {
          enqueueSnackbar(err.message, {
            variant: "error",
          });
        });
      fetchIconsList();
    },
    [enqueueSnackbar, fetchIconsList, configStore, t]
  );

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 2,
        label: t("screens.iconsList.column.title"),
        dataKey: "iconUrl",
        render: (iconUrl: string) => {
          return (
            <Image
              src={iconUrl}
              alt={iconUrl}
              size={50}
              width={50}
              fit={"contain"}
              align={"left"}
            />
          );
        },
      },
      {
        width: 90,
        flexGrow: 0,
        label: t("common.action"),
        dataKey: "id",
        render: (id: number) => {
          return (
            <PopupState variant="popover" popupId="demo-popup-popover">
              {(popupState) => (
                <div>
                  <IconButton color="error" {...bindTrigger(popupState)}>
                    <DeleteIcon />
                  </IconButton>
                  <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                  >
                    <Typography sx={{ p: 1 }}>
                      {t("common.deletePopUp")}
                      <IconButton
                        color="error"
                        sx={{ p: 0 }}
                        onClick={() => handleDeleteProgram(id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Typography>
                  </Popover>
                </div>
              )}
            </PopupState>
          );
        },
      },
    ],
    [handleDeleteProgram, t]
  );

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px" align="top">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.iconsList.breadcrumb.home")}
          </BreadcrumbItem>
          <BreadcrumbItem active>
            {t("screens.iconsList.breadcrumb.icons")}
          </BreadcrumbItem>
        </Breadcrumb>

        <PageHeader
          title={t("screens.iconsList.pageHeader.title")}
          buttonEnable={true}
          buttonText={t("screens.iconsList.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      </Content>
      {configStore.isLoading ? (
        <Loading />
      ) : !configStore.isLoading && configStore.iconsList.length > 0 ? (
        <Content fullHeight align="top" mt={[null, null, null, "0px"]}>
          <div
            style={{
              flex: "1 1 auto",
              width: "100%",
              height: `${(configStore.iconsList.length + 1) * 50}px`,
              display: "flex",
              marginTop: "20px",
            }}
          >
            <VirtualizedTable
              rowCount={configStore.iconsList.length}
              rowGetter={({ index }) => configStore.iconsList[index]}
              columns={columns}
              rowStyle={(row) =>
                row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
              }
            />
          </div>
        </Content>
      ) : (
        <EmptySpace
          title={t("screens.iconsList.emptyData")}
          buttonEnable={true}
          buttonText={t("screens.iconsList.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      )}

      <FileUploadModal
        isOpen={isModalOpen}
        onClose={modalHandler}
        onFileUploaded={handleUploadCompleted}
        maxFiles={1}
        accept="image/jpeg, image/png"
      />
    </Layout>
  );
});

export default IconsList;
